import { Mesh, type Material } from 'three';
import { ACETATES, appearanceLook, type AppearanceChoice } from '../config/appearance';
import type { FrameLook } from '../config/frames';
import { AcetateDressing } from './AcetateOptics';
import type { FrameObject } from './FrameObject';
import { ScreenLensMaterial } from './LensOptics';
import type { Renderer } from './Renderer';
import { prepareFrame } from './ShaderWarmup';

/**
 * Materials only: preserves geometry, UVs, pivots, camera, pose and user scale.
 * « Original » keeps the product photo materials; a library acetate dresses the
 * primary meshes with its optics (see AcetateOptics) and is fully removed again
 * on the next choice or on dispose.
 */
/**
 * A110Y: dressings taken off are kept, undrawn, for this many more choices. Their materials hold
 * the compiled programs of their optics family (plate or flat × see-through or solid, lit and
 * transmission); with the one worn, the four families can stay compiled for the whole session.
 */
const RETIRED_DRESSINGS = 3;

export class FrameAppearance {
  private originals = new Map<Mesh, Material | Material[]>();
  private dressing: AcetateDressing | null = null;
  private readonly retired: AcetateDressing[] = [];
  private selected = 'original';
  private generation = 0;
  private disposed = false;
  constructor(private frame: FrameObject) {
    frame.model.traverse(o => {
      if (o instanceof Mesh && o.userData.acetateRole === 'primary') this.originals.set(o, o.material);
    });
  }
  /**
   * Applies a choice. The previous dressing is taken off at once (its extra draws must not stay),
   * but its materials are retired, not disposed (A110Y): the programs of an optics family are
   * shared by all its acetates, and three destroys a program the moment its last material is
   * disposed — disposing first made every tap on a colour recompile two large programs
   * synchronously (0.2–0.8 s freeze on a phone). With `renderer`, the retirement waits for the new
   * dressing to be compiled, so the oldest retired dressing is never disposed before the program it
   * shares with the new one is held by both.
   */
  apply(choice: AppearanceChoice, renderer?: Renderer | null, opacity = 1): FrameLook {
    const look = appearanceLook(this.frame.spec, choice);
    if (choice.acetate !== this.selected) {
      const previous = this.dressing;
      for (const [mesh, original] of this.originals) mesh.material = original;
      this.dressing = null;
      previous?.detach();
      const acetate = ACETATES.find(a => a.id === choice.acetate);
      // The plate texture loads lazily, on selection (and stays cached for a return: AcetateOptics).
      if (acetate) this.dressing = new AcetateDressing(this.frame.model, [...this.originals.keys()], acetate);
      this.selected = choice.acetate;
      this.frame.invalidateOpacity();
      if (previous) {
        if (renderer && this.dressing) {
          const generation = ++this.generation;
          void prepareFrame(renderer, this.frame, opacity, () => this.disposed || generation !== this.generation)
            .catch(() => undefined)
            .finally(() => this.retire(previous));
        } else this.retire(previous);
      }
    }
    const visited = new Set<Material>();
    this.frame.model.traverse(o => {
      if (!(o instanceof Mesh)) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        if (m instanceof ScreenLensMaterial && !visited.has(m)) { m.setLook(look); visited.add(m); }
      }
    });
    return look;
  }
  /** Keeps a dressing's materials alive for a while (programs), disposing the oldest past the cap. */
  private retire(dressing: AcetateDressing): void {
    if (this.disposed) { dressing.dispose(); return; }
    dressing.detach();
    if (!this.retired.includes(dressing)) this.retired.push(dressing);
    while (this.retired.length > RETIRED_DRESSINGS) this.retired.shift()!.dispose();
  }
  /** Dressings kept for their programs (tests). */
  get retiredCount(): number {
    return this.retired.length;
  }
  /** Restore owned originals before FrameObject releases all geometry/materials. */
  dispose(): void {
    this.disposed = true;
    this.undress();
    for (const d of this.retired) d.dispose();
    this.retired.length = 0;
    this.originals.clear();
  }
  private undress(): void {
    for (const [mesh, original] of this.originals) mesh.material = original;
    this.frame.invalidateOpacity();
    this.dressing?.dispose();
    this.dressing = null;
  }
}
